import { DividaResource } from '@/models/resources';
import { acertoOrdenouDividasRecomendacao } from '@/modules/events/acerto';
import { DividasOrderingService } from '../../DividasOrderingService';
import { type OrdenacaoBackendResponse } from '../../OrdenacaoBackendService';
import { IOrdenacaoStrategy } from '../interfaces/IOrdenacaoStrategy';
import { TipoOrdenacao } from '../types/TipoOrdenacao';

/**
 * Estratégia de ordenação baseada na modelagem do backend da Acerto.
 *
 * A ordenação é definida por um payload vindo do endpoint de ordenação,
 * que contém a lista de dívidas na ordem recomendada.
 */
// eslint-disable-next-line import/prefer-default-export
export class OrdenacaoPorModelagemAcertoStrategy implements IOrdenacaoStrategy {
  /**
   * Payload estático compartilhado entre todas as instâncias da strategy.
   * Deve ser atualizado sempre que uma nova resposta de ordenação for recebida.
   */
  private static ordenacaoRecomendada: OrdenacaoBackendResponse | null = null;

  private static eventoDisparado = false;

  private orderingService: DividasOrderingService;

  constructor(orderingService: DividasOrderingService) {
    this.orderingService = orderingService;
  }

  /**
   * Atualiza o payload de ordenação recomendado pelo backend.
   * Pode ser chamado, por exemplo, logo após `ordenacaoBackendService.buscarOrdenacao()`.
   */
  static atualizarOrdenacaoRecomendada(
    payload: OrdenacaoBackendResponse | null,
  ): void {
    OrdenacaoPorModelagemAcertoStrategy.ordenacaoRecomendada = payload;
  }

  /**
   * Retorna o payload de ordenação recomendado pelo backend (leitura apenas).
   */
  static obterOrdenacaoRecomendada(): OrdenacaoBackendResponse | null {
    return OrdenacaoPorModelagemAcertoStrategy.ordenacaoRecomendada;
  }

  static resetEventoDisparado(): void {
    OrdenacaoPorModelagemAcertoStrategy.eventoDisparado = false;
  }

  private static normalizarApi(api: string): string {
    return api.replace(/^mc-/i, '');
  }

  ordenar(lote: DividaResource[], tipoOrdenacao: TipoOrdenacao): DividaResource[] {
    if (tipoOrdenacao !== TipoOrdenacao.MODELAGEM_ACERTO) {
      throw new Error(`Tipo de ordenação ${tipoOrdenacao} não suportado por esta estratégia`);
    }

    if (lote.length === 0) {
      return [];
    }

    const payload = OrdenacaoPorModelagemAcertoStrategy.ordenacaoRecomendada;

    if (payload && !OrdenacaoPorModelagemAcertoStrategy.eventoDisparado) {
      acertoOrdenouDividasRecomendacao('modelagem_acerto', payload.ordemDividas);
      OrdenacaoPorModelagemAcertoStrategy.eventoDisparado = true;
    }

    if (!payload?.ordemDividas?.length) {
      // Sem payload válido, cai no fallback de maior receita via contexto
      return this.orderingService.ordenarLotes(
        [lote],
        [TipoOrdenacao.MAIOR_RECEITA],
      );
    }

    // Mapa: "api-id" -> posição na lista recomendada
    const posicoesMap = new Map<string, number>();

    payload.ordemDividas.forEach((item, index) => {
      const key = DividasOrderingService.calcularKey(item.credor, item.dividaId);
      posicoesMap.set(key, index);
    });
    // Ordena de forma estável: primeiro as dívidas presentes no payload, na ordem dada,
    // depois as demais mantendo a ordem relativa original.
    return [...lote]
      .map((divida, originalIndex) => {
        const key = DividasOrderingService.calcularKey(
          OrdenacaoPorModelagemAcertoStrategy.normalizarApi(divida.api),
          divida.id,
        );
        const posicaoRecomendada = posicoesMap.get(key);
        const rank = posicaoRecomendada ?? Number.MAX_SAFE_INTEGER;

        return {
          divida,
          originalIndex,
          rank,
        };
      })
      .sort((a, b) => {
        if (a.rank !== b.rank) {
          return a.rank - b.rank;
        }

        // Nenhum dos dois está no payload (ou ambos com mesmo rank)
        // → mantém ordem de chegada original
        return a.originalIndex - b.originalIndex;
      })
      .map((wrapper) => wrapper.divida);
  }
}
